import {HttpBackend, HttpClient, HttpEvent, HttpHandler, HttpHeaders, HttpRequest} from '@angular/common/http';
import {firstValueFrom, map, Observable, retry, timer} from 'rxjs';
import {Injectable} from '@angular/core';
import {ACCESS_TOKEN_KEY} from '../models/storageKeys';


export const retryWithDelay = (duration: any) => {
  return retry({
    delay: (error, retryCount) => {
      const sleep = retryCount * duration;
      return timer(sleep);
    },
    resetOnSuccess: true,
  });
}

class HasuraHttpHandler implements HttpHandler {


  constructor(private httpHandler: HttpHandler) {
  }

  handle(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${sessionStorage.getItem(ACCESS_TOKEN_KEY)}`);
    return this.httpHandler.handle(req.clone({url: `${req.url}`, headers}));
  }
}


export class HasuraHttp extends HttpClient {

  // @ts-ignore
  private protocol = window.location.protocol.replace('http', 'ws').replace('https', 'wss');
  // @ts-ignore
  private host = window.location.host;


  constructor(backend: HttpBackend, private subdomain: string) {
    super(new HasuraHttpHandler(backend));
  }

  /*
    const result = await this.svr.runSql("select * from bpms.beds")
   */
  async runSql(sql: string) {
    return firstValueFrom(super.post(`/${this.subdomain}/query`, {
      "type": "run_sql",
      "args": {
        "sql": sql
      }
    }).pipe(map((res: any) => {
      if (!!res.errors) {
        const messages = res.errors.map((e: any) => e.message);
        throw Error(messages.join('; '))
      }
      return res
    })));
  }


  /*
    const result = await this.svr.select("bpms_beds",
      ["bed", "accessible"],
      {hall: {_eq: "SR11"}},
      {hall: 'asc', bed: 'desc'},
      3
    );
   */
  async select(table: string, fields: string, whereClause: any = {}, orderBy: any = {}, limit: number = null) {
    const query = `query MyQuery($where: ${table}_bool_exp = {}, $order_by: [${table}_order_by!] = {}, $limit: Int = null) {
        ${table}(where: $where, order_by: $order_by, limit: $limit) {
          ${fields}
        }
      }`
    const data = await this.execute(query, {
      "where": whereClause,
      "order_by": orderBy,
      "limit": limit,
    })
    return data[table];
  }

  async selectOne(table: string, fields: string, whereClause: any = {}) {
    const rows = await this.select(table, fields, whereClause, {}, 1);
    return rows.length > 0 ? rows[0] : null;
  }

  async insert(table: string, objects: any[], returns: any[] = []) {
    let returning = ''
    if (returns.length > 0) {
      returning = `
        returning {
          ${returns.join(' ')}
        }
      `
    }
    const query = `mutation MyMutation($objects: [${table}_insert_input!] = {}) {
                                  insert_${table}(objects: $objects) {
                                    affected_rows
                                    ${returning}
                                  }
                                }`
    const data = await this.execute(query, {
      "objects": objects
    })
    return data[`insert_${table}`];
  }

  async upsert(schema: string, table: string, objects: any[], updateColumns: string) {
    const schemaTable = `${schema}_${table}`;
    const query = `mutation MyMutation($objects: [${schemaTable}_insert_input!] = {}) {
                                  insert_${schemaTable}(objects: $objects, on_conflict: {constraint: ${table}_pkey, update_columns: [${updateColumns}]}) {
                                    affected_rows
                                  }
                                }`
    const data = await this.execute(query, {
      "objects": objects
    })
    return data[`insert_${schemaTable}`];
  }

  async updateMany(table: string, updates: any[]) {
    const query = `mutation MyMutation($updates: [${table}_updates!]!){
                                  update_${table}_many(updates: $updates) {
                                    affected_rows
                                  }
                                }`
    const data = await this.execute(query, {
      "updates": updates
    })
    return data[`update_${table}_many`];
  }

  async updateByPK(table: string, pk_columns: any, return_field: string[], object: any) {
    const query = `mutation MyMutation($_set: ${table}_set_input!, $pk_columns: ${table}_pk_columns_input!){
                                  update_${table}_by_pk(pk_columns: $pk_columns, _set: $_set) {
                                    ${return_field.join('\n')}
                                  }
                                }`
    const data = await this.execute(query, {
      "pk_columns": pk_columns,
      "_set": object
    })
    return data[`update_${table}_by_pk`];
  }

  async deleteMany(table: string, whereClause: any = {}) {
    const query = `mutation MyMutation($where: ${table}_bool_exp = {}) {
                                  delete_${table}(where: $where) {
                                    affected_rows
                                    returning {
                                      id
                                    }
                                  }
                                }`
    const data = await this.execute(query, {
      "where": whereClause
    })
    return data[`delete_${table}`];
  }

  async execute(query: string, variables: any): Promise<any> {
    const data = {
      "query": query,
      "variables": variables
    }
    return firstValueFrom(super.post(`/${this.subdomain}/graphql`, data).pipe(map((res: any) => {
      if (!!res.errors) {
        const messages = res.errors.map((e: any) => e.message);
        throw Error(messages.join('; '))
      }
      return res?.data
    })));
  }

  /*
    ks = toSignal(this.svr.subscription(`
    subscription MySubscription {
      bpms_halls {
        abbr
      }
    }`));
   */
  public subscription(query: string, variables = {}, keepalive = true): Observable<any> {
    const wsUrl = `${this.protocol}//${this.host}/${this.subdomain}-subscription`;
    const obs = new Observable<any>((subscriber) => {
      const ws = new WebSocket(wsUrl, ["graphql-ws"]);
      ws.onerror = (err) => {
        subscriber.error(new Error(`web socket error occurred: ${err}`,));
      };
      ws.onopen = () => {
        const init_connection = {
          payload: {
            headers: {
              "content-type": "application/json",
              'authorization': `Bearer ${sessionStorage.getItem(ACCESS_TOKEN_KEY)}`
            },
            lazy: true,
          },
          type: "connection_init"
        }
        ws.send(JSON.stringify(init_connection));

        const data = {
          id: "1",
          payload: {
            extensions: {},
            operationName: "MySubscription",
            query: query,
            variables: variables
          },
          type: "start"
        }
        ws.send(JSON.stringify(data));

      };
      ws.onclose = () => {
        if (keepalive) {
          subscriber.error(new Error('Socket closed unexpectedly'));
        } else {
          subscriber.complete();
        }
      };
      ws.onmessage = (e: any) => {
        try {
          const data = JSON.parse(e.data as string);
          if (data?.type === 'data') {
            subscriber.next(data.payload.data);
          }
        } catch (err) {
          console.log("Data parsing error:", err);
        }
      };
      return () => {
        ws.close();
      };
    });
    if (keepalive) {
      return obs.pipe(retryWithDelay(1000));
    }
    return obs;
  }
}

export class HasuraTransaction {
  private mutations: { header: string, snippet: string }[] = [];
  private variables: any = {};
  private counter = 0;
  private executed = false;

  constructor(private client: HasuraHttp) {
  }

  private checkOpen() {
    if (this.executed) {
      throw new Error('Transaction has already been executed');
    }
  }

  private next(): number {
    return ++this.counter;
  }

  addInsert(table: string, objects: any[], returns: string = null): string {
    this.checkOpen();
    const n = this.next();
    const alias = `insert_${table}_${n}`;
    const v = `objects_${n}`;
    const returning = returns ? `returning { ${returns} }` : '';
    this.mutations.push({
      header: `$${v}: [${table}_insert_input!]!`,
      snippet: `${alias}: insert_${table}(objects: $${v}) {
                 affected_rows
                 ${returning}
               }`
    });
    this.variables[v] = objects;
    return alias;
  }

  addUpsert(table: string, objects: any[], constraint: string, updateColumns: string[], returns: string = null): string {
    this.checkOpen();
    const n = this.next();
    const alias = `upsert_${table}_${n}`;
    const vObj = `objects_${n}`;
    const vConst = `constraint_${n}`;
    const vCols = `columns_${n}`;
    const returning = returns ? `returning { ${returns} }` : '';
    this.mutations.push({
      header: `$${vObj}: [${table}_insert_input!]!, $${vConst}: ${table}_constraint!, $${vCols}: [${table}_update_column!]`,
      snippet: `${alias}: insert_${table}(objects: $${vObj}, on_conflict: {constraint: $${vConst}, update_columns: $${vCols}}) {
                 affected_rows
                 ${returning}
               }`
    });
    this.variables[vObj] = objects;
    this.variables[vConst] = constraint;
    this.variables[vCols] = updateColumns;
    return alias;
  }

  addUpdateByPk(table: string, pkColumns: any, object: any, returns: string = 'id'): string {
    this.checkOpen();
    const n = this.next();
    const alias = `update_${table}_by_pk_${n}`;
    const vPk = `pk_columns_${n}`;
    const vSet = `set_${n}`;
    this.mutations.push({
      header: `$${vPk}: ${table}_pk_columns_input!, $${vSet}: ${table}_set_input!`,
      snippet: `${alias}: update_${table}_by_pk(pk_columns: $${vPk}, _set: $${vSet}) {
                 ${returns}
               }`
    });
    this.variables[vPk] = pkColumns;
    this.variables[vSet] = object;
    return alias;
  }

  addUpdateMany(table: string, updates: any[], returning: string = 'id'): string {
    this.checkOpen();
    const n = this.next();
    const alias = `update_${table}_many_${n}`;
    const v = `updates_${n}`;
    this.mutations.push({
      header: `$${v}: [${table}_updates!]!`,
      snippet: `${alias}: update_${table}_many(updates: $${v}) {
                 affected_rows
                 returning { ${returning} }
               }`
    });
    this.variables[v] = updates;
    return alias;
  }

  addDeleteMany(table: string, where: any, returning: string = 'id'): string {
    this.checkOpen();
    const n = this.next();
    const alias = `delete_${table}_${n}`;
    const v = `where_${n}`;
    this.mutations.push({
      header: `$${v}: ${table}_bool_exp!`,
      snippet: `${alias}: delete_${table}(where: $${v}) {
                 affected_rows
                 returning { ${returning} }
               }`
    });
    this.variables[v] = where;
    return alias;
  }

  async execute(): Promise<any> {
    this.checkOpen();
    this.executed = true;
    if (this.mutations.length === 0) {
      return {};
    }
    const headers = this.mutations.map(m => m.header).join(', ');
    const bodies = this.mutations.map(m => m.snippet).join('\n');
    const query = `mutation TransactionMutation(${headers}) {\n${bodies}\n}`;
    return this.client.execute(query, this.variables);
  }
}

@Injectable()
export class MosHasura extends HasuraHttp {
  constructor(backend: HttpBackend) {
    super(new HasuraHttpHandler(backend), 'mos-hasura');
  }
}

@Injectable()
export class KlnHasura extends HasuraHttp {
  constructor(backend: HttpBackend) {
    super(new HasuraHttpHandler(backend), 'klnt-hasura');
  }
}
